import { useState } from 'react';
import { X, Download, Upload, FileText, CheckCircle, Info, Plus, Loader2, Mail, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ViubuxPlayUploader = ({ isOpen, onClose, user, token }) => {
  const [activeStep, setActiveStep] = useState(1); // 1: Form, 2: Upload
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    author: user?.full_name || '',
    type: 'video',
    category: '',
    description: '',
    videoUrl: '',
    audioUrl: '',
    thumbnail: '',
    coverImage: '',
    language: 'Español',
    duration: '',
    isFree: 'false',
    price: '0',
    memberPrice: '0',
    viubuxOriginal: 'false',
    viubuxConnect: 'true',
    subscribersOnly: 'false',
    isbn: '',
    publisher: '',
    genres: '',
    tags: '',
    audience: 'adults',
    releaseDate: new Date().toISOString().split('T')[0],
    narrator: '',
    editorial: '',
    producer: '',
    director: '',
    compositor: ''
  });

  const contentTypes = [
    { value: 'video', label: 'Video / Película' },
    { value: 'audio', label: 'Audiolibro / Podcast' },
    { value: 'course', label: 'Curso' },
    { value: 'conference', label: 'Conferencia' },
    { value: 'documentary', label: 'Documental' }
  ];

  const categories = [
    'Ficción', 'No Ficción', 'Educación', 'Entretenimiento', 'Negocios', 
    'Desarrollo Personal', 'Salud', 'Tecnología', 'Arte', 'Música', 
    'Cocina', 'Deportes', 'Infantil', 'Ciencia', 'Historia', 'Otro'
  ];

  const audiences = [
    { value: 'kids', label: 'Niños' },
    { value: 'teens', label: 'Adolescentes' },
    { value: 'adults', label: 'Adultos' },
    { value: 'all', label: 'Todas las edades' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.title.trim()) {
      toast.error('El título es requerido');
      return;
    }
    if (!formData.author.trim()) {
      toast.error('El nombre del autor es requerido');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('La descripción es requerida');
      return;
    }
    if (!formData.category) {
      toast.error('La categoría es requerida');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(
        `${API}/viubuxplay/submit-content`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('¡Formulario enviado! Revisa tu correo.');
        setSubmitted(true);
        setActiveStep(2);
      }
    } catch (error) {
      console.error('Error submitting content:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar el formulario');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setActiveStep(1);
    setSubmitted(false);
    setFormData({
      title: '',
      author: user?.full_name || '',
      type: 'video',
      category: '',
      description: '',
      videoUrl: '',
      audioUrl: '',
      thumbnail: '',
      coverImage: '',
      language: 'Español',
      duration: '',
      isFree: 'false',
      price: '0',
      memberPrice: '0',
      viubuxOriginal: 'false',
      viubuxConnect: 'true',
      subscribersOnly: 'false',
      isbn: '',
      publisher: '',
      genres: '',
      tags: '',
      audience: 'adults',
      releaseDate: new Date().toISOString().split('T')[0],
      narrator: '',
      editorial: '',
      producer: '',
      director: '',
      compositor: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-b from-gray-900 to-black border border-rose-500/30 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-rose-600 via-pink-600 to-fuchsia-600 p-6 rounded-t-2xl z-10">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                <Upload className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="gotham-bold text-2xl text-white">Publicar en Viubux Play</h2>
                <p className="text-white/80 text-sm mt-1">Videos, Movies, Conferencias y más</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="close-viubuxplay-uploader"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
          
          {/* Steps indicator */}
          <div className="flex items-center gap-4 mt-4">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${activeStep === 1 ? 'bg-white text-rose-600' : 'bg-white/20 text-white'}`}>
              <span className="w-6 h-6 rounded-full bg-current/20 flex items-center justify-center text-sm font-bold">1</span>
              <span className="text-sm font-medium">Información</span>
            </div>
            <div className="w-8 h-0.5 bg-white/30"></div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${activeStep === 2 ? 'bg-white text-rose-600' : 'bg-white/20 text-white'}`}>
              <span className="w-6 h-6 rounded-full bg-current/20 flex items-center justify-center text-sm font-bold">2</span>
              <span className="text-sm font-medium">Subir Contenido</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {activeStep === 1 && (
            <>
              {/* Instructions */}
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                  <p className="text-white/80 text-sm">
                    Completa la información de tu contenido. Al enviar, recibirás un <strong className="text-rose-300">correo de confirmación</strong> con los detalles de tu solicitud.
                  </p>
                </div>
              </div>

              {/* Form */}
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="bg-black/30 border border-white/10 rounded-xl p-5">
                  <h3 className="gotham-bold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-400" />
                    Información Básica
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Título *</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        placeholder="Nombre de tu contenido"
                        className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-rose-500 focus:outline-none"
                        data-testid="content-title-input"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Autor *</label>
                      <input
                        type="text"
                        value={formData.author}
                        onChange={(e) => handleInputChange('author', e.target.value)}
                        placeholder="Tu nombre"
                        className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-rose-500 focus:outline-none"
                        data-testid="content-author-input"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Tipo de Contenido *</label>
                      <select
                        value={formData.type}
                        onChange={(e) => handleInputChange('type', e.target.value)}
                        className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-rose-500 focus:outline-none"
                        data-testid="content-type-select"
                      >
                        {contentTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Categoría *</label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-rose-500 focus:outline-none"
                        data-testid="content-category-select"
                      >
                        <option value="">Selecciona una categoría</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-white/70 text-sm mb-2">Descripción *</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Describe tu contenido..."
                      rows={3}
                      className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-rose-500 focus:outline-none resize-none"
                      data-testid="content-description-input"
                    />
                  </div>
                </div>

                {/* Media Info */}
                <div className="bg-black/30 border border-white/10 rounded-xl p-5">
                  <h3 className="gotham-bold text-white mb-4 flex items-center gap-2">
                    <Upload className="w-5 h-5 text-blue-400" />
                    Información del Medio
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Duración</label>
                      <input
                        type="text"
                        value={formData.duration}
                        onChange={(e) => handleInputChange('duration', e.target.value)}
                        placeholder="Ej: 2h 30m"
                        className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-rose-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Idioma</label>
                      <select
                        value={formData.language}
                        onChange={(e) => handleInputChange('language', e.target.value)}
                        className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-rose-500 focus:outline-none"
                      >
                        <option value="Español">Español</option>
                        <option value="English">English</option>
                        <option value="Portugués">Portugués</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Audiencia</label>
                      <select
                        value={formData.audience}
                        onChange={(e) => handleInputChange('audience', e.target.value)}
                        className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-rose-500 focus:outline-none"
                      >
                        {audiences.map(aud => (
                          <option key={aud.value} value={aud.value}>{aud.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Fecha de Lanzamiento</label>
                      <input
                        type="date"
                        value={formData.releaseDate}
                        onChange={(e) => handleInputChange('releaseDate', e.target.value)}
                        className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-rose-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-white/70 text-sm mb-2">Géneros / Tags</label>
                    <input
                      type="text"
                      value={formData.genres}
                      onChange={(e) => handleInputChange('genres', e.target.value)}
                      placeholder="Ej: Drama, Ficción, Suspenso (separados por coma)"
                      className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-rose-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Additional Info (Collapsible) */}
                <details className="bg-black/30 border border-white/10 rounded-xl">
                  <summary className="p-5 cursor-pointer gotham-bold text-white flex items-center gap-2">
                    <Plus className="w-5 h-5 text-green-400" />
                    Información Adicional (Opcional)
                  </summary>
                  <div className="px-5 pb-5 space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/70 text-sm mb-2">ISBN</label>
                        <input
                          type="text"
                          value={formData.isbn}
                          onChange={(e) => handleInputChange('isbn', e.target.value)}
                          placeholder="978-3-16-148410-0"
                          className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-rose-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Editorial</label>
                        <input
                          type="text"
                          value={formData.editorial}
                          onChange={(e) => handleInputChange('editorial', e.target.value)}
                          placeholder="Nombre de la editorial"
                          className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-rose-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Narrador</label>
                        <input
                          type="text"
                          value={formData.narrator}
                          onChange={(e) => handleInputChange('narrator', e.target.value)}
                          placeholder="Nombre del narrador"
                          className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-rose-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Director</label>
                        <input
                          type="text"
                          value={formData.director}
                          onChange={(e) => handleInputChange('director', e.target.value)}
                          placeholder="Nombre del director"
                          className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-rose-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Productor</label>
                        <input
                          type="text"
                          value={formData.producer}
                          onChange={(e) => handleInputChange('producer', e.target.value)}
                          placeholder="Nombre del productor"
                          className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-rose-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Compositor</label>
                        <input
                          type="text"
                          value={formData.compositor}
                          onChange={(e) => handleInputChange('compositor', e.target.value)}
                          placeholder="Nombre del compositor"
                          className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-rose-500 focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                </details>
              </div>
            </>
          )}

          {activeStep === 2 && (
            <>
              {/* Success Message */}
              {submitted && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-green-400" />
                  </div>
                  <h3 className="gotham-bold text-xl text-white mb-2">¡Información Enviada!</h3>
                  <p className="text-white/70 mb-4">
                    Revisa tu correo <strong className="text-green-400">{user?.email}</strong> para confirmar tu solicitud.
                  </p>
                  <div className="bg-green-900/30 rounded-lg p-4 text-left">
                    <p className="text-green-300 text-sm">
                      <strong>Título:</strong> {formData.title}<br/>
                      <strong>Tipo:</strong> {formData.type}<br/>
                      <strong>Categoría:</strong> {formData.category}
                    </p>
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="bg-rose-500/20 p-3 rounded-lg flex-shrink-0">
                    <Info className="w-6 h-6 text-rose-400" />
                  </div>
                  <div>
                    <h3 className="gotham-bold text-lg text-white mb-2">Ahora sube tu contenido multimedia</h3>
                    <ol className="text-white/80 verdana-regular space-y-2 list-decimal list-inside">
                      <li>Usa el formulario de abajo para subir tu archivo (video, audio, etc.)</li>
                      <li>Confirma escribiendo el <strong className="text-rose-300">mismo correo electrónico</strong> con el que te diste de alta</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Practical Tips */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5">
                <h4 className="gotham-bold text-white mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-400" />
                  Consejos Prácticos
                </h4>
                <div className="space-y-3 text-white/80 verdana-regular text-sm">
                  <p>Nombra tu archivo multimedia con el siguiente formato:</p>
                  <div className="bg-black/30 rounded-lg p-4 font-mono text-sm">
                    <span className="text-green-400">{formData.title || 'Nombre'}</span> - 
                    <span className="text-yellow-400"> {formData.author || 'Tu Nombre'}</span> - 
                    <span className="text-purple-400"> {formData.type || 'Tipo'}</span> - 
                    <span className="text-blue-400"> {formData.releaseDate || 'Fecha'}</span>
                  </div>
                  <p className="text-white/70 mt-2">
                    De preferencia ponlo en una <strong className="text-cyan-400">carpeta comprimida .zip</strong>
                  </p>
                </div>
              </div>

              {/* Upload iframe */}
              <div className="rounded-xl overflow-hidden border border-rose-500/30">
                <div className="bg-rose-900/30 px-4 py-3 border-b border-rose-500/30">
                  <h4 className="gotham-medium text-white flex items-center gap-2">
                    <Upload className="w-5 h-5 text-rose-400" />
                    Subir Archivo Multimedia
                  </h4>
                </div>
                <div className="bg-white p-2">
                  <iframe 
                    src="https://soundbyimage.app.box.com/f/4d15b6ba910943aa88cce80a3e27c312" 
                    height="450" 
                    width="100%"
                    className="border-0 rounded"
                    title="Viubux Play Content Uploader"
                    data-testid="viubuxplay-upload-iframe"
                  />
                </div>
              </div>

              {/* Auto-publish notice */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-green-300 text-sm verdana-regular">
                  <strong>*Si tu membresía se encuentra activa</strong>, tu contenido se publicará en automático en los próximos minutos y te llegará una notificación a tu correo.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-black/90 backdrop-blur border-t border-rose-500/20 p-4 flex justify-between">
          {activeStep === 2 && !submitted && (
            <Button
              onClick={() => setActiveStep(1)}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              Volver a editar
            </Button>
          )}
          {(activeStep === 1 || submitted) && <div></div>}
          
          <div className="flex gap-3">
            <Button
              onClick={handleClose}
              variant="outline"
              className="border-rose-500/50 text-rose-400 hover:bg-rose-500/20"
            >
              {submitted ? 'Cerrar' : 'Cancelar'}
            </Button>
            {activeStep === 1 && (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar y Continuar
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViubuxPlayUploader;
